import { useCallback, useEffect } from "react"
import { useParams, useNavigate, useLocation } from "react-router-dom"
import _ from "lodash"
import { useTheme, Box } from "@mui/material"

import { useMap } from "hooks/map/useMap"
import { useOrdinalMap } from "hooks/ordinal"
import {
  useFlyToMap,
  useGetUserLocationView,
  useGetMapViewFromUrl,
} from "hooks/map"
import { useVenue } from "providers/venue"
import { useGeoLocation } from "providers/geolocation"
import { pluralize } from "utils/string"

import { FeatureChipToggleBar } from "components/domains/features/ChipToggleBar"
import { PageCard } from "components/card"
import { VenueSelector } from "components/VenueSelector"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h"
import { OrdinalSelector } from "components/OrdinalSelector"
import { MyLocationButton } from "components/Button"
import { convertMapViewObjectToPath } from "utils/map"
import { SidebarMenu } from "components/navigation/SidebarMenu"
import { usePageSideBarLayout } from "providers/ui/hooks"
import { OrdinalSelectSplide } from "components/OrdinalSelector/OrdinalSelectSplide"

export const MapOrdinalKioskHPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
}) => {
  const { userLocation } = useGeoLocation()
  const navigate = useNavigate()
  const { ordinal } = useParams()
  const { search: searchQueryString } = useLocation()
  const { isMultiOrdinalVenue, isMultiVenueProject, venues } = useVenue()
  const {
    viewingVenueId,
    viewingVenueView,
    viewingVenueLevels,
    quickSearchCategories,
    onChangeVenue,
    locationVenueId,
    locationOrdinal,
  } = useMap()

  const parsedOrdinal = Number(ordinal)
  const hasUserLocationAtCurrentFloor = locationOrdinal === parsedOrdinal

  useOrdinalMap(parsedOrdinal)
  const userLocationView = useGetUserLocationView()
  const { mapView } = useGetMapViewFromUrl()
  useFlyToMap(mapView || userLocationView || viewingVenueView)

  const theme = useTheme()
  const { styleOverrides = {} } = theme.components[`AppMap`] || {}

  const {
    toolbarContainer,
    quickLinkContainer, //TODO:: planning about quick search's working flow later
    venueSelector,
    ordinalSelectMenu,
    ordinalSelectSplide,
    locationButtonContainer,
  } = styleOverrides

  const onClickOrdinal = useCallback(
    (ordinal) => {
      navigate(
        `/maps/${
          ordinal !== null ? `ordinal/${ordinal}${searchQueryString}` : ""
        }`,
        { replace: true }
      )
    },
    [navigate, searchQueryString]
  )

  const handleClickQuickSearch = useCallback(
    (type, category) => {
      //This condition prevent navigation when ordinal or viewingVenueId is undefined
      if (_.isNil(ordinal) || _.isNil(viewingVenueId)) return
      const pluralType = pluralize(type)
      navigate(
        `/maps/${pluralType}/${category}?from-ordinal=${ordinal}&venue=${viewingVenueId}`
      )
    },
    [navigate, ordinal, viewingVenueId]
  )

  const handleClickMyLocation = useCallback(() => {
    //This condition prevent navigation when ordinal is undefined
    if (_.isNil(ordinal) || !userLocation) return
    const userOrdinal = _.get(userLocation, "properties.ordinal", 0)
    const mapViewPath = convertMapViewObjectToPath(userLocationView)
    navigate(`/maps/ordinal/${userOrdinal}?mv=${mapViewPath}`, {
      replace: true,
    })
  }, [navigate, ordinal, userLocationView, userLocation])

  useEffect(() => {
    if (hasUserLocationAtCurrentFloor) handleClickMyLocation()
  }, [hasUserLocationAtCurrentFloor, handleClickMyLocation])

  usePageSideBarLayout({
    sidebar: {
      open: false,
    },
  })

  return (
    //@ts-ignore
    <Layout
      aside={
        <PageCard>
          <SidebarMenu AppNavigation={AppNavigation} />
        </PageCard>
      }
    >
      <Box sx={toolbarContainer}>
        <Box sx={quickLinkContainer}>
          <FeatureChipToggleBar
            categories={quickSearchCategories}
            onClick={handleClickQuickSearch}
          />
        </Box>
      </Box>
      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelectMenu}>
          <OrdinalSelector
            selectedOrdinal={parsedOrdinal}
            levels={viewingVenueLevels}
            onClickOrdinal={onClickOrdinal}
            locationOrdinal={locationOrdinal}
            currentVenue={locationVenueId}
          />
        </Box>
      )}
      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelectSplide}>
          <OrdinalSelectSplide
            selectedOrdinal={parsedOrdinal}
            levels={viewingVenueLevels}
            onChangeOrdinal={onClickOrdinal}
          />
        </Box>
      )}
      {isMultiVenueProject && (
        <Box sx={venueSelector}>
          <VenueSelector
            selectedVenue={viewingVenueId}
            venues={venues}
            onClickVenue={onChangeVenue}
            currentVenue={locationVenueId}
          />
        </Box>
      )}

      {userLocation && (
        <Box sx={locationButtonContainer}>
          <MyLocationButton onClick={handleClickMyLocation} />
        </Box>
      )}
    </Layout>
  )
}
